/**
 * Время реплик на дорожке оригинала: где у реплики конец, куда встаёт новая («+») и куда
 * возвращается испорченная («вернуть окно»). Живёт у провайдера: им пользуются и он, и
 * компоненты строки.
 */
import { AudioTrack } from "./types";
import { voicingSounds } from "./dubSounding";
import { MIN_CUE_MS } from "./editorErrors";

const finite = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

/** Начало реплики на дорожке оригинала: как её время хранит файл (start_rus_milli). */
const cueStart = (track: AudioTrack): number | null => {
    const start = finite(track.start_rus_milli) ? track.start_rus_milli : track.start;
    return finite(start) && start >= 0 ? start : null;
};

/**
 * Конец реплики — end_rus_milli, если он после начала. Иначе время испорчено (длина −158000
 * у реплики fdcde2a1), и строить от него окно нельзя: «начало + длина» уводило «+» на минуты
 * назад.
 */
const cueEnd = (track: AudioTrack): number | null => {
    const start = cueStart(track);
    if (start === null) return null;
    if (finite(track.end_rus_milli) && track.end_rus_milli > start) return track.end_rus_milli;
    if (finite(track.length) && track.length > 0) return start + track.length;
    return null;
};

/**
 * Промежуток для «+»: от конца нажатой реплики до начала следующей (или до конца папки).
 * Ошибка — словами, чтобы диалог сказал, почему добавить нельзя.
 */
export const addCueWindow = ({
    track,
    tracks,
    durationFull,
}: {
    track: AudioTrack;
    tracks: AudioTrack[];
    durationFull: number;
}): { start: number; end: number } | { error: string } => {
    const start = cueEnd(track);
    if (start === null) {
        return {
            error:
                "У этой реплики испорчено время (конец не после начала) — окно от неё построить нельзя. " +
                "Сначала верните ей окно или добавьте реплику от соседней.",
        };
    }
    const index = tracks.findIndex((one) => one.id === track.id);
    const next = index >= 0 ? tracks[index + 1] : undefined;
    const nextStart = next ? cueStart(next) : null;
    let end: number | null = nextStart;
    if (end === null) {
        if (next) {
            return { error: "У следующей реплики испорчено время — промежуток до неё не определить." };
        }
        if (!finite(durationFull) || durationFull <= 0) {
            return { error: "Длина папки ещё не известна — дождитесь загрузки звука." };
        }
        end = durationFull;
    } else if (finite(durationFull) && durationFull > 0) {
        end = Math.min(end, durationFull);
    }
    const startMs = Math.round(start);
    const endMs = Math.round(end);
    if (endMs - startMs < MIN_CUE_MS) {
        return { error: "Между этой и следующей репликой нет места (меньше 0,1 с)." };
    }
    return { start: startMs, end: endMs };
};

/**
 * Время реплики оригинала испорчено: длина не больше нуля, начало отрицательное или сервер сам
 * пометил её timing_invalid. Такая реплика рисуется полоской в 25 px и выглядит обычной.
 */
export const cueTimingBroken = (track: AudioTrack): boolean =>
    track.timing_invalid === true || cueEnd(track) === null;

/**
 * «Вернуть окно»: от конца предыдущей реплики с целым временем (или начала папки) до начала
 * следующей (или конца папки). Короче 0,1 с — отказ словами.
 */
export const restoreWindow = (
    tracks: AudioTrack[],
    index: number,
    durationFull: number,
): { start: number; end: number } | { error: string } => {
    let start = 0;
    for (let i = index - 1; i >= 0; i--) {
        const end = cueEnd(tracks[i]);
        if (end !== null) {
            start = end;
            break;
        }
    }
    let end: number | null = null;
    for (let i = index + 1; i < tracks.length; i++) {
        const next = cueStart(tracks[i]);
        if (next !== null && !cueTimingBroken(tracks[i])) {
            end = next;
            break;
        }
    }
    if (end === null) {
        if (!finite(durationFull) || durationFull <= 0) {
            return { error: "длина папки ещё не известна — дождитесь загрузки звука" };
        }
        end = durationFull;
    }
    const startMs = Math.round(start);
    const endMs = Math.round(end);
    if (endMs - startMs < MIN_CUE_MS) {
        return { error: "между соседними репликами нет места (меньше 0,1 с)" };
    }
    return { start: startMs, end: endMs };
};

/** Окно реплики оригинала, если её время цело (иначе null — окно вернёт «вернуть окно»). */
export const cueWindow = (track: AudioTrack): { start: number; end: number } | null => {
    if (cueTimingBroken(track)) return null;
    const start = cueStart(track);
    const end = cueEnd(track);
    return start === null || end === null ? null : { start: Math.round(start), end: Math.round(end) };
};

// --- Время перевода (json пары) так, как его читает сервер ------------------------------------

/**
 * Миллисекунды так, как их читает сервер (`utils._as_ms`): число или строка-число; всё прочее
 * (null, строка-время, мусор) — null.
 */
export const timeMs = (value: unknown): number | null => {
    if (typeof value === "number") return Number.isFinite(value) ? value : null;
    if (typeof value === "string" && value.trim() !== "") {
        const number = Number(value.trim());
        return Number.isFinite(number) ? number : null;
    }
    return null;
};

/** 'H:MM:SS.fff' → мс; отрицательная или нечитаемая строка — null (как `_time_string_ms`). */
const timeStringMs = (value: unknown): number | null => {
    if (typeof value !== "string" || value.trim().startsWith("-")) return null;
    const match = /^(\d+):(\d{1,2}):(\d{1,2}(?:[.,]\d+)?)$/.exec(value.trim());
    if (!match) return null;
    return (Number(match[1]) * 3600 + Number(match[2]) * 60 + Number(match[3].replace(",", "."))) * 1000;
};

/** Время перевода: место озвучки, начало слота (нет ключа — нет поля) и конец слота. */
export interface PairTiming {
    start: number | null;
    slotStart?: number | null;
    slotEnd: number | null;
}

type PairMeta = {
    start?: unknown;
    start_rus_milli?: unknown;
    end_rus_milli?: unknown;
    end_rus?: unknown;
    length?: unknown;
};

/**
 * Время json перевода, прочитанное как на сервере озвучки: конец слота — число end_rus_milli, а
 * строка end_rus — только запасной источник (`synt_audio._cue_end_ms`).
 */
export const pairTimingOf = (meta: PairMeta): PairTiming => {
    const numberEnd = timeMs(meta.end_rus_milli);
    const timing: PairTiming = {
        start: timeMs(meta.start),
        slotEnd: numberEnd !== null ? numberEnd : timeStringMs(meta.end_rus),
    };
    if (meta.start_rus_milli !== undefined) timing.slotStart = timeMs(meta.start_rus_milli);
    return timing;
};

export type VoicingType = "tts" | "original_ru";

/** Что во времени перевода не даст его озвучить. */
export type SynthTimingRefusal = "start" | "slot_start" | "slot_end" | "order";

/**
 * Почему сервер озвучки откажет по времени — зеркало `synth_refusal` (translate-python,
 * services_fastapi/synt_audio.py; C4), без проверки текста: место озвучки, начало и конец слота —
 * не меньше нуля; для оригинального звука ещё конец слота позже места. Длина не проверяется: это
 * длина дубля, её пересчитывает сама переозвучка, а начало позже русского конца — законная
 * раскладка (6 691 реплика).
 */
export const synthTimingRefusal = (timing: PairTiming, type: VoicingType): SynthTimingRefusal | null => {
    if (timing.start === null || timing.start < 0) return "start";
    if (timing.slotStart !== undefined && (timing.slotStart === null || timing.slotStart < 0)) return "slot_start";
    if (timing.slotEnd === null || timing.slotEnd < 0) return "slot_end";
    if (type === "original_ru" && timing.slotEnd <= timing.start) return "order";
    return null;
};

/**
 * Окно перевода испорчено: отрицательные (или нечитаемые) место, начало или конец слота — это
 * чинит «вернуть окно». Длина ≤ 0 при целом окне — не окно, а длина дубля: её пересчитывает
 * переозвучка, и окно ей возвращать нельзя (оно стёрло бы законную раскладку).
 */
export const pairWindowBroken = (timing: PairTiming): boolean =>
    synthTimingRefusal(timing, "tts") !== null;

/**
 * Что сломано во времени перевода — зеркало `utils.cue_timing_problem(source=False)`: им новый
 * сервер помечает timing_invalid; здесь — для старого сервера, который не метит сам.
 */
export const pairTimingProblem = (meta: PairMeta): string | null => {
    const start = timeMs(meta.start);
    if (start === null || start < 0) return `начало ${String(meta.start)}`;
    if (meta.end_rus_milli !== undefined) {
        const end = timeMs(meta.end_rus_milli);
        if (end === null || end < 0) return `конец ${String(meta.end_rus_milli)}`;
    }
    if (meta.length !== undefined) {
        const length = timeMs(meta.length);
        if (length === null || length <= 0) return `длина ${String(meta.length)}`;
    }
    return null;
};

/**
 * Озвучки, которые плеер пропустит: файл есть, а длины нет (расписание берёт только реплики с
 * положительной длиной). Раньше такая озвучка молчала, и нигде не было видно почему. Снятая с
 * озвучки (⊘) молчит нарочно и говорит это своей меткой — здесь её нет.
 */
export const unplayableVoicings = (tracks: AudioTrack[]): AudioTrack[] =>
    tracks.filter((track) => voicingSounds(track) && !(finite(track.length) && track.length > 0));
